import React, { useCallback, useContext, useMemo, useState } from 'react';
import classnames from 'classnames';
import { useValueChange } from '@leverege/ui-hooks';
import { IdGen, Pane, Label, NumericInput } from '@leverege/ui-elements';
import { ApiProvider } from '@leverege/api-redux';
import I18N from '@leverege/i18n';
import { AttributeSelector } from '@leverege/ui-attributes';
import Autosuggest from "../../util/Autosuggest.js";
import InputFactory from "../../InputFactory.js";
import Model from "../model/TermsAggModel.js";
import S from '../Shared.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.dataViewers.aggregations.TermsAggEditor');
export default function TermsAggEditor(props) {
  const {
    value,
    attributes,
    delegate,
    onChange,
    eventData
  } = props;
  const apiPath = `v1/interface/${delegate.getUrl(true)}`;
  const api = useContext(ApiProvider.Context);
  const [ids] = useState(IdGen.createIds('attr', 10));
  const [options, setOptions] = useState([]);
  const values = useMemo(() => {
    return attributes.filter(attr => attr?.blueprint?.field != null && attr?.getOption?.('filterable') === true).map(attr => {
      return {
        name: attr.displayName,
        value: attr.id,
        attribute: attr
      };
    }).sort((itemA, itemB) => {
      if (itemA.name > itemB.name) {
        return 1;
      }
      return -1;
    });
  }, [attributes]);
  const [bpValue, setBpVal] = useState(() => {
    return values.find(val => val.attribute.name === value?.attrName)?.attribute?.name;
  });
  const onModelChange = useValueChange(Model, props);
  const onChangeField = useCallback(evt => {
    const attribute = values.find(val => val.attribute.name === evt.value)?.attribute;
    if (!attribute) {
      return;
    }
    const fieldType = attribute?.source?.fieldType || attribute?.source?.attribute?.searchType;
    let nModel = Model.setField(value, attribute.source.field);
    nModel = Model.setFieldType(nModel, fieldType || 'string');
    nModel = Model.setAttrName(nModel, attribute.name);
    onChange({
      value: nModel,
      data: eventData
    });
    setBpVal(evt.value);
  }, [eventData, onChange, value, values]);
  const onFetchOptions = useCallback(({
    value: search
  }) => {
    if (search && search.length > 1) {
      // eslint-disable-next-line react/destructuring-assignment
      api.child(apiPath).list({
        search: search ? `*${search}*` : '',
        aggs: {
          options: {
            type: 'terms',
            field: Model.getField(value),
            fieldType: Model.getFieldType(value)
          }
        }
      }).then(res => {
        const opts = res?.aggs?.options?.buckets?.map?.(bucket => bucket.key) || [];
        setOptions(opts);
      });
    } else {
      setOptions([]);
    }
  }, [api, value, apiPath]);
  const onChangeMissing = useCallback(evt => {
    if (typeof evt.value === 'string') {
      if (evt.value.trim().length === 0) {
        onModelChange({
          data: evt.data,
          value: null
        });
        return;
      }
    }
    onModelChange(evt);
  }, [onModelChange]);
  let idn = 0;
  return /*#__PURE__*/_jsxs(Pane, {
    variant: "editor",
    className: classnames(S.grid, S.sub),
    children: [/*#__PURE__*/_jsx(Label, {
      htmlFor: ids[idn],
      children: t('field')
    }), /*#__PURE__*/_jsx(AttributeSelector, {
      showFilter: true,
      id: ids[idn++],
      onChange: onChangeField,
      eventData: "setField",
      value: bpValue,
      values: values.map(val => val.attribute)
    }), /*#__PURE__*/_jsx(Label, {
      htmlFor: ids[idn],
      children: t('size')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[idn++],
      value: Model.getSize(value),
      eventData: "setSize",
      onChange: onModelChange
    }), bpValue && /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx(Label, {
        htmlFor: ids[idn],
        children: t('exclude')
      }), /*#__PURE__*/_jsx(Autosuggest, {
        className: S.autosuggest,
        value: Model.getExcludes(value),
        options: options,
        eventData: "setExcludes",
        onChange: onModelChange,
        onFetchOptions: onFetchOptions,
        id: ids[idn++]
      })]
    }), bpValue && /*#__PURE__*/_jsxs(React.Fragment, {
      children: [/*#__PURE__*/_jsx(Label, {
        htmlFor: ids[idn],
        children: t('include')
      }), /*#__PURE__*/_jsx(Autosuggest, {
        className: S.autosuggest,
        value: Model.getIncludes(value),
        options: options,
        eventData: "setIncludes",
        onChange: onModelChange,
        onFetchOptions: onFetchOptions,
        id: ids[idn++]
      })]
    }), /*#__PURE__*/_jsx(Label, {
      htmlFor: ids[idn],
      children: t('minCount')
    }), /*#__PURE__*/_jsx(NumericInput, {
      id: ids[idn++],
      value: Model.getMinCount(value),
      eventData: "setMinCount",
      onChange: onModelChange
    }), /*#__PURE__*/_jsx(Label, {
      children: t('missingValue')
    }), InputFactory.create(Model.getFieldType(value), {
      eventData: 'setMissing',
      value: Model.getMissing(value),
      onChange: onChangeMissing
    })]
  });
}